import React, { useMemo, useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { DsAlert, DsButton, DsSpinner } from "../../../../design-system";
import { attendanceService } from "../../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusFromRows,
} from "../../../Common/import-validation";

const TEMPLATE_HEADERS = [
  "Employee Code",
  "Employee Name",
  "Worked Date",
  "Hours Worked",
  "Credit Days",
  "Expires On",
  "Source Type",
  "Remarks",
];

const HEADER_MAP = [
  { key: "employeeCode", re: /^(employee\s*code|emp\s*code|code)$/i },
  { key: "employeeName", re: /^(employee\s*name|emp\s*name|name)$/i },
  { key: "workedDate", re: /^(worked\s*date|work\s*date|source\s*date|date)$/i },
  { key: "hoursWorked", re: /^(hours\s*worked|hours|hrs)$/i },
  { key: "creditDays", re: /^(credit\s*days|days|balance|comp\s*off\s*days)$/i },
  { key: "expiresOn", re: /^(expires\s*on|expiry\s*date|expiry|valid\s*till|valid\s*until)$/i },
  { key: "sourceType", re: /^(source\s*type|source|type)$/i },
  { key: "remarks", re: /^(remarks|notes|reason|comments)$/i },
];

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase();
}

function parseSheetToRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheetName =
          workbook.SheetNames.find((n) => /comp\s*off|opening/i.test(n)) ||
          workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: true });
        if (!matrix.length) {
          resolve({ rows: [], headers: [] });
          return;
        }
        const headers = (matrix[0] || []).map((h) => String(h || "").trim());
        const colIndex = {};
        headers.forEach((header, idx) => {
          const match = HEADER_MAP.find((m) => m.re.test(normalizeHeader(header)));
          if (match && colIndex[match.key] == null) colIndex[match.key] = idx;
        });
        if (colIndex.employeeCode == null) {
          reject(
            new Error("Sheet must include an Employee Code column.")
          );
          return;
        }
        const rows = [];
        for (let i = 1; i < matrix.length; i += 1) {
          const cells = matrix[i] || [];
          const blank = cells.every((c) => c == null || String(c).trim() === "");
          if (blank) continue;
          const pick = (key) => {
            const idx = colIndex[key];
            return idx == null ? "" : cells[idx];
          };
          const toCell = (value) => {
            if (value instanceof Date && !Number.isNaN(value.getTime())) {
              const y = value.getFullYear();
              const m = String(value.getMonth() + 1).padStart(2, "0");
              const d = String(value.getDate()).padStart(2, "0");
              return `${y}-${m}-${d}`;
            }
            return value;
          };
          rows.push({
            __row: i + 1,
            employeeCode: pick("employeeCode"),
            employeeName: pick("employeeName"),
            workedDate: toCell(pick("workedDate")),
            hoursWorked: pick("hoursWorked"),
            creditDays: pick("creditDays"),
            expiresOn: toCell(pick("expiresOn")),
            sourceType: pick("sourceType"),
            remarks: pick("remarks"),
          });
        }
        resolve({ rows, headers });
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function CompOffOpeningImportPanel({ darkMode = false }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [imported, setImported] = useState(null);

  const instruction = useMemo(
    () =>
      "Import Comp Off lots earned before PaceHRM go-live. Each row becomes an earned (credited) balance with its own worked date and expiry.",
    []
  );

  const downloadTemplate = async () => {
    try {
      const res = await attendanceService.downloadCompOffOpeningTemplate();
      const blob = res?.data instanceof Blob ? res.data : new Blob([res.data]);
      triggerBlobDownload(blob, "Comp_Off_Opening_Balance_Template.xlsx");
    } catch (err) {
      console.error(err);
      toast.error("Could not download the Comp Off template.");
    }
  };

  const resetFile = () => {
    setFile(null);
    setReview(null);
    setImported(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validateFile = async (chosen) => {
    const nextFile = chosen || file;
    if (!nextFile) {
      toast.error("Choose an Excel file to validate.");
      return;
    }
    setBusy(true);
    setImported(null);
    try {
      const parsed = await parseSheetToRows(nextFile);
      if (!parsed.rows.length) {
        toast.error("No data rows found in the sheet.");
        return;
      }
      const res = await attendanceService.importCompOffOpening({
        rows: parsed.rows,
        dryRun: true,
      });
      const data = res?.data || res;
      const built = buildImportValidationResult({
        validRecords: data.validRecords || [],
        invalidRecords: data.invalidRecords || [],
        sourceHeaders: TEMPLATE_HEADERS,
      });
      setReview(built);
      if (built.failedCount === 0) {
        toast.success(`${built.successCount} row(s) ready to import.`);
      } else {
        toast.error(
          `${built.failedCount} row(s) failed validation. Review the list before importing.`
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Validation failed.");
    } finally {
      setBusy(false);
    }
  };

  const commitRows = async (records) => {
    const rows = (records || [])
      .map((r) => r.record)
      .filter((r) => r && r.employeeCode);
    if (!rows.length) {
      toast.error("No successful rows to import.");
      return;
    }
    setBusy(true);
    try {
      const res = await attendanceService.importCompOffOpening({
        rows,
        dryRun: false,
        commit: true,
      });
      const data = res?.data || res;
      if (data?.invalidRecords?.length) {
        const built = buildImportValidationResult({
          validRecords: data.validRecords || [],
          invalidRecords: data.invalidRecords || [],
          sourceHeaders: TEMPLATE_HEADERS,
        });
        setReview(built);
        toast.error("Some rows could not be imported. Review the failed list.");
        return;
      }
      setImported(data.imported || rows.length);
      setReview(null);
      toast.success(`Imported ${data.imported || rows.length} Comp Off lot(s).`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ds-page-panel mx-auto max-w-4xl space-y-4 p-4 lg:p-6">
      <div>
        <h2 className="ds-h3">Import Comp Off opening balance</h2>
        <p className="ds-body mt-1 text-[hsl(var(--ds-muted-fg))]">{instruction}</p>
      </div>

      <DsAlert tone="info">
        Use this for balances earned outside PaceHRM (legacy / pre go-live). Live Comp Off
        continue to earn from approved Regularise / punch on Weekly Off or holiday. Avail
        remains Leave type CO.
      </DsAlert>

      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-4">
        <p className="ds-caption font-semibold text-[hsl(var(--ds-fg))]">Template columns</p>
        <ul className="ds-body mt-2 list-disc space-y-1 pl-5 text-[hsl(var(--ds-muted-fg))]">
          <li>Employee Code, Worked Date, Credit Days, Expires On — required</li>
          <li>Hours Worked, Source Type (opening / weekly_off / holiday / extra_hours), Remarks — optional</li>
          <li>Dates as YYYY-MM-DD or DD/MM/YYYY. Duplicate lots are rejected.</li>
        </ul>
      </div>

      {imported != null && (
        <DsAlert tone="success">{imported} Comp Off lot(s) credited to the ledger.</DsAlert>
      )}

      {busy && !review ? (
        <div className="flex items-center gap-2 py-6 text-[hsl(var(--ds-muted-fg))]">
          <DsSpinner />
          <span className="ds-body">Validating file…</span>
        </div>
      ) : review ? (
        <ImportValidationReview
          result={review}
          busy={busy}
          title="Comp Off opening validation"
          entityLabel="lot"
          onBack={resetFile}
          onDownloadReport={() =>
            downloadValidationStatusFromRows(
              review.allRows,
              "Comp_Off_Opening_Validation.xlsx"
            )
          }
          onImportAll={() => commitRows(review.successRows)}
          onImportSuccessfulOnly={() => commitRows(review.successRows)}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <DsButton type="button" variant="outline" onClick={downloadTemplate} className="gap-2">
              <Download size={16} />
              Download template
            </DsButton>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const next = e.target.files?.[0] || null;
                setFile(next);
                setImported(null);
              }}
            />
            <DsButton
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={16} />
              Choose file
            </DsButton>
            {file ? (
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">{file.name}</span>
            ) : null}
          </div>
          <DsButton
            type="button"
            disabled={!file || busy}
            onClick={() => validateFile()}
          >
            Validate &amp; preview
          </DsButton>
        </div>
      )}
      <p className={darkMode ? "sr-only" : "sr-only"}>Comp Off opening import</p>
    </div>
  );
}
